import { isDataStarter } from './catalogue';
import type { FlowGraph } from './graph';
import {
  type EdgeType,
  type GraphIndex,
  indexGraph,
  mismatch,
  portKey,
  resolveTypes,
  topologicalOrder,
} from './resolve';
import { DATA_TYPE_LABELS } from './types';

export const ISSUE_CODES = [
  'DUPLICATE_ID',
  'UNKNOWN_NODE_TYPE',
  'COMING_SOON',
  'INVALID_CONFIG',
  'UNKNOWN_PORT',
  'TYPE_MISMATCH',
  'MIXED_TYPES',
  'CYCLE',
  'MISSING_INPUT',
  'NO_STARTER',
  'MISSING_GUARDRAIL',
  'DEAD_END',
  'NO_HOOK_CAPTIONS',
  // From personaIssues: they need the workspace's personas.
  'PERSONA_NOT_TICKED',
  'PERSONA_NO_ACCOUNTS',
  'NO_PERSONA_ACCOUNTS',
] as const;
export type IssueCode = (typeof ISSUE_CODES)[number];

export interface Issue {
  code: IssueCode;
  /** Errors block Test run, Run and Activate; warnings don't. */
  severity: 'error' | 'warning';
  message: string;
  nodeId?: string;
  edgeId?: string;
}

const PUBLISH = 'output.schedulePublish';
const GUARDRAIL = 'guardrail.brandGuardrails';
// Its winner feeds the next run (an "On Pick winner" trigger), not this one.
const PICK_WINNER = 'experiment.pickWinner';
const BUILD_CAROUSELS = 'generate.buildCarousels';

/**
 * Everything wrong with a graph, in a stable order. A draft may be saved with
 * issues; only errors stop it from running.
 *
 * Review isn't checked here: when the workspace requires it, publishing holds
 * each post in the review queue whether or not the flow has Human approval.
 */
export function validateGraph(graph: FlowGraph): Issue[] {
  const issues: Issue[] = [];
  const error = (
    code: IssueCode,
    message: string,
    at: Pick<Issue, 'nodeId' | 'edgeId'> = {},
  ) => issues.push({ code, severity: 'error', message, ...at });

  duplicateIds(graph, error);
  const index = indexGraph(graph);

  for (const { node, definition } of index.nodes.values()) {
    if (!definition) {
      error('UNKNOWN_NODE_TYPE', `Unknown node type ${node.type} v${node.typeVersion}`, {
        nodeId: node.id,
      });
      continue;
    }
    const name = node.label ?? definition.label;
    if (definition.status === 'soon') {
      error('COMING_SOON', `${name} isn’t available yet`, { nodeId: node.id });
    }
    const parsed = definition.config.safeParse(node.config);
    if (!parsed.success) {
      for (const problem of parsed.error.issues) {
        error('INVALID_CONFIG', `${name}: ${problem.message}`, { nodeId: node.id });
      }
    }
  }

  const types = resolveTypes(graph, index);
  for (const edge of graph.edges) {
    const from = index.nodes.get(edge.source.nodeId)?.definition;
    const to = index.nodes.get(edge.target.nodeId)?.definition;
    const outPort = from?.outputs.find((port) => port.id === edge.source.port);
    const inPort = to?.inputs.find((port) => port.id === edge.target.port);
    if (!from || !to || !outPort || !inPort) {
      if (
        index.nodes.has(edge.source.nodeId) &&
        index.nodes.has(edge.target.nodeId) &&
        (!from || !to)
      ) {
        continue; // already reported as an unknown node type
      }
      error(
        'UNKNOWN_PORT',
        'This connection points at a node or port that doesn’t exist',
        {
          edgeId: edge.id,
        },
      );
      continue;
    }
    const type = types.get(portKey(edge.source.nodeId, edge.source.port));
    const reason = mismatch(
      inPort,
      type ?? (outPort.type === 'trigger' ? 'trigger' : undefined),
      to,
    );
    if (reason) error('TYPE_MISMATCH', reason, { edgeId: edge.id });
  }

  mixedTypes(index, types, error);

  const { cyclic } = topologicalOrder(index);
  for (const id of cyclic) {
    error('CYCLE', 'This node is part of a loop; flows run in one direction', {
      nodeId: id,
    });
  }

  for (const { node, definition } of index.nodes.values()) {
    if (!definition) continue;
    const connected = new Set(
      (index.incoming.get(node.id) ?? []).map((edge) => edge.target.port),
    );
    for (const port of definition.inputs) {
      if (port.optional || port.accepts === 'trigger' || connected.has(port.id)) continue;
      const which = definition.inputs.length > 1 ? ` (${port.label ?? port.id})` : '';
      error(
        'MISSING_INPUT',
        `Connect something into ${node.label ?? definition.label}${which}`,
        {
          nodeId: node.id,
        },
      );
    }
  }

  const starters = [...index.nodes.values()].filter(
    ({ definition }) => definition && isDataStarter(definition),
  );
  if (starters.length === 0) {
    error(
      'NO_STARTER',
      'Add a starter — the flow needs somewhere for content to come from',
    );
  }

  for (const { node } of index.nodes.values()) {
    if (node.type !== PUBLISH) continue;
    if (unguardedPath(index, node.id, GUARDRAIL)) {
      error(
        'MISSING_GUARDRAIL',
        'Every path into Schedule & publish must pass Brand guardrails',
        {
          nodeId: node.id,
        },
      );
    }
  }

  for (const { node, definition } of index.nodes.values()) {
    if (!definition || definition.category === 'output' || node.type === PICK_WINNER)
      continue;
    const feeds = definition.outputs.some((port) => port.type !== 'trigger');
    if (feeds && (index.outgoing.get(node.id) ?? []).length === 0) {
      issues.push({
        code: 'DEAD_END',
        severity: 'warning',
        message: `${node.label ?? definition.label} doesn’t lead anywhere; its results are only kept in the run`,
        nodeId: node.id,
      });
    }
  }

  for (const { node } of index.nodes.values()) {
    if (node.type !== BUILD_CAROUSELS || node.config.captionsCollectionId) continue;
    issues.push({
      code: 'NO_HOOK_CAPTIONS',
      severity: 'warning',
      message:
        'Hook captions is empty, so the first slide of each carousel has no text. Pick a caption collection.',
      nodeId: node.id,
    });
  }

  return issues;
}

export function hasErrors(issues: readonly Issue[]): boolean {
  return issues.some((issue) => issue.severity === 'error');
}

type Report = (
  code: IssueCode,
  message: string,
  at?: Pick<Issue, 'nodeId' | 'edgeId'>,
) => void;

function duplicateIds(graph: FlowGraph, error: Report) {
  const seen = new Set<string>();
  for (const { id } of [...graph.nodes, ...graph.edges]) {
    if (seen.has(id)) error('DUPLICATE_ID', `Two items share the id ${id}`);
    seen.add(id);
  }
}

/** A pass-through node fed two different types can't say what it emits. */
function mixedTypes(
  index: GraphIndex,
  types: Map<string, EdgeType | 'trigger' | undefined>,
  error: Report,
) {
  for (const { node, definition } of index.nodes.values()) {
    if (!definition?.outputs.some((port) => port.type === 'same')) continue;
    const bases = new Set(
      (index.incoming.get(node.id) ?? [])
        .map((edge) => types.get(portKey(edge.source.nodeId, edge.source.port)))
        .filter((type): type is EdgeType => type !== undefined && type !== 'trigger')
        .map((type) => DATA_TYPE_LABELS[type.base]),
    );
    if (bases.size > 1) {
      error(
        'MIXED_TYPES',
        `${node.label ?? definition.label} gets ${[...bases].join(' and ')}; it passes one type through`,
        { nodeId: node.id },
      );
    }
  }
}

/**
 * Whether content can reach `nodeId` without passing a node of type `gate`:
 * walks upstream and stops at gates; reaching a node with no inputs means
 * some path skipped it.
 */
function unguardedPath(index: GraphIndex, nodeId: string, gate: string): boolean {
  const seen = new Set<string>();
  const stack = (index.incoming.get(nodeId) ?? []).map((edge) => edge.source.nodeId);
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const entry = index.nodes.get(id);
    if (!entry || entry.node.type === gate) continue;
    const upstream = (index.incoming.get(id) ?? []).filter((edge) => {
      const port = entry.definition?.inputs.find((p) => p.id === edge.target.port);
      return port?.accepts !== 'trigger';
    });
    if (upstream.length === 0) return true;
    stack.push(...upstream.map((edge) => edge.source.nodeId));
  }
  return false;
}
